import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import GridLayout, { LayoutItem, Layout } from "react-grid-layout"
import { gridBounds, minMaxSize, transformStrategy } from "react-grid-layout/core"
import { RiDragDropLine } from "@remixicon/react"
import { useTranslation } from "react-i18next"

import { usePdfBuilderStore, selectCurrentPage, itemsToLayouts } from "@/stores/pdfBuilder"
import type { Page } from "@/types/pdfBuilder"
import { createRowAlignConstraint, widthSnapConstraint } from "../constants/gridSnap"
import { buildItemsFromPalette, isPaletteDragEvent } from "../utils/paletteItems"

import CanvasItemComponent from "./CanvasItem"
import type { CanvasItem } from "@/types/pdfBuilder"
import GridOverlay from "./GridOverlay"

import "react-grid-layout/css/styles.css"

interface ReportCanvasProps {
  page?: Page
  selectedItemId?: string | null
  /**
   * 바깥 래퍼가 캔버스에 건 CSS transform: scale() 배율 (PDF 편집 화면의 줌). 기본 1.
   * react-grid-layout 은 getBoundingClientRect(배율이 곱해진 화면 좌표)로 드래그 위치를 계산하므로
   * 배율을 알려주지 않으면 클릭만 해도 아이템이 배율만큼 튀고, 200% 이상에서는 커서를 벗어나 선택이 안 된다.
   */
  scale?: number
}

// A4 dimensions at 96 DPI
const A4_WIDTH_PORTRAIT = 794
const A4_HEIGHT_PORTRAIT = 1123
const A4_WIDTH_LANDSCAPE = 1123
const A4_HEIGHT_LANDSCAPE = 794

// Grid configuration
const COLS = 12
const ROW_HEIGHT = 30
const MARGIN: [number, number] = [10, 10]
const CONTAINER_PADDING: [number, number] = [20, 20]
// 키보드 크기 조절을 스토어에 적용하기 전에 기다리는 시간 — 이 안에 다음 키가 오면 모아서 한 번에 적용한다
const KEY_APPLY_DELAY_MS = 200
// 직전 적용(재배치)이 이보다 오래 걸렸을 때만 스피너를 먼저 그린다 — 가벼운 페이지에서는 표시 없이 바로 적용한다
const SLOW_APPLY_MS = 120

// 배치의 내용 서명 — 순서와 무관하게 같은 배치면 같은 문자열
const layoutKeyOf = (layout: readonly Pick<LayoutItem, "i" | "x" | "y" | "w" | "h">[]) =>
  [...layout]
    .map((l) => `${l.i}:${l.x},${l.y},${l.w},${l.h}`)
    .sort()
    .join("|")

export default function ReportCanvas(props: ReportCanvasProps) {
  const storePage = usePdfBuilderStore(selectCurrentPage)
  const storeSelectedItemId = usePdfBuilderStore((s) => s.selectedItemId)
  const selectItem = usePdfBuilderStore((s) => s.selectItem)
  const removeItem = usePdfBuilderStore((s) => s.removeItem)
  const updateItemLayout = usePdfBuilderStore((s) => s.updateItemLayout)
  const updateItem = usePdfBuilderStore((s) => s.updateItem)
  const addItem = usePdfBuilderStore((s) => s.addItem)
  const { t } = useTranslation()

  const page = props.page ?? storePage
  const selectedItemId =
    props.selectedItemId !== undefined ? props.selectedItemId : storeSelectedItemId
  const scale = props.scale && props.scale > 0 ? props.scale : 1

  // 기본 transform 전략에 배율만 덧씌운다 — GridItem 의 drag start(부모 rect 차이 ÷ scale)·react-draggable·
  // react-resizable 이 같은 배율을 쓴다. 라이브러리의 createScaledStrategy 는 calcDragPosition 이 부모
  // 오프셋을 빼지 않은 뷰포트 좌표를 돌려줘 드래그 시작 시 다른 격자로 튀므로 쓰지 않는다.
  const positionStrategy = useMemo(() => ({ ...transformStrategy, scale }), [scale])

  // 팔레트 카드의 브라우저 기본(HTML5) 드래그를 받는다. dragenter/dragleave 는 자식 요소를 지날 때마다
  // 짝으로 오므로 깊이를 세어 "위에 있음"을 판단한다.
  const [dragDepth, setDragDepth] = useState(0)
  const isOver = dragDepth > 0

  const isLandscape = page?.config.orientation === "landscape"
  const canvasWidth = isLandscape ? A4_WIDTH_LANDSCAPE : A4_WIDTH_PORTRAIT
  const canvasHeight = isLandscape ? A4_HEIGHT_LANDSCAPE : A4_HEIGHT_PORTRAIT

  const items = page?.items ?? []
  const pageId = page?.id

  // 크기를 바꿀 때 같은 줄 이웃의 아래끝에 맞춰 주는 제약 — 지금 배치를 ref 로 넘겨, 제약 배열이 매번
  // 새로 만들어져 격자가 다시 세팅되는 일이 없게 한다 (2026-10-02 요청)
  const layoutRef = useRef<LayoutItem[]>([])
  layoutRef.current = useMemo(() => itemsToLayouts(items), [items])
  const constraints = useMemo(
    () => [
      gridBounds,
      minMaxSize,
      widthSnapConstraint,
      createRowAlignConstraint(() => layoutRef.current),
    ],
    [],
  )

  // 우리가 props 로 넘긴 배치들의 서명(최근 몇 개). react-grid-layout 은 props 배치가 바뀔 때마다 자기 상태를
  // 맞춘 뒤 onLayoutChange 로 그 배치를 그대로 되돌려 준다 — 스토어가 연속으로 두 번 바뀌면(표 자동 높이 등)
  // 첫 번째 낡은 배치가 뒤늦게 되돌아와 스토어를 되돌리고, 그 변화가 다시 되돌아오며 두 값 사이를 영원히 오간다
  // ("Maximum update depth", 2026-09-23 재현). 우리가 넘긴 것과 같은 배치는 메아리로 보고 무시한다.
  // 라이브러리가 실제로 바꾼 배치(드래그·리사이즈·충돌 이동)는 넘긴 것과 다르므로 그대로 반영된다.
  const sentLayoutKeysRef = useRef<string[]>([])
  const layouts = useMemo(() => itemsToLayouts(items), [items])
  useEffect(() => {
    sentLayoutKeysRef.current = [layoutKeyOf(layouts), ...sentLayoutKeysRef.current].slice(0, 10)
  }, [layouts])

  // 격자 내용의 실제 높이(px). react-grid-layout 배치 규칙과 같은 식으로 계산한다.
  // 아이템 아래끝 = containerPadding + (y + h) * (rowHeight + marginY) - marginY
  const contentHeight = useMemo(() => {
    if (items.length === 0) {
      return canvasHeight
    }
    const maxBottomUnits = Math.max(...items.map((it) => it.y + it.h))

    return CONTAINER_PADDING[1] * 2 + maxBottomUnits * (ROW_HEIGHT + MARGIN[1]) - MARGIN[1]
  }, [items, canvasHeight])

  // 페이지 경계선 위치. 내용이 A4 여러 장 분량이면 장마다 선을 반복해 그린다.
  // 내용이 한 장에 들어오면 1장 끝 선만 긋고 장 번호는 붙이지 않는다 —
  // 번호를 붙이면 있지도 않은 다음 장이 있는 것처럼 보여 실제 출력 장수와 어긋난다.
  const pageBreaks = useMemo(() => {
    // 상한을 둔다 — 배치 계산이 잘못돼 내용 높이가 비정상으로 커져도
    // Array.from 이 "Invalid array length" 로 화면 전체를 죽이지 않게 한다
    const MAX_BREAKS = 50
    const raw = Math.ceil(contentHeight / canvasHeight) - 1
    const withContent = Math.min(MAX_BREAKS, Math.max(0, Number.isFinite(raw) ? raw : 0))
    const lines = Math.max(1, withContent)

    return Array.from({ length: lines }, (_, i) => ({
      top: (i + 1) * canvasHeight,
      // 이 선 뒤로 내용이 이어지는 경우에만 장 번호를 붙인다
      label: i < withContent ? `${t("page", "Page")} ${i + 2}` : "",
    }))
  }, [contentHeight, canvasHeight, t])

  // 종이는 늘 장 단위로 끝낸다 — 내용이 장 중간에서 끝나면 그 아래를 빈 여백으로 채워 한 장을 마무리한다.
  // 종이가 내용에서 뚝 잘려 보이던 것을 없앤다 (2026-10-02 요청)
  const paperHeight = useMemo(() => {
    const pages = Math.max(1, Math.ceil(contentHeight / canvasHeight))

    return Number.isFinite(pages) ? pages * canvasHeight : canvasHeight
  }, [contentHeight, canvasHeight])

  // 고른 컴포넌트와 세로로 겹치는 것들 = 같은 줄. 줄을 맞추라고 옅은 테두리를 보여 준다 (2026-10-02 요청)
  const rowPeerIds = useMemo(() => {
    const selected = items.find((item) => item.id === selectedItemId)
    if (!selected) {
      return new Set<string>()
    }

    return new Set(
      items
        .filter(
          (item) =>
            item.id !== selected.id &&
            item.y < selected.y + selected.h &&
            item.y + item.h > selected.y,
        )
        .map((item) => item.id),
    )
  }, [items, selectedItemId])

  // 끌기·크기조절 중에는 자동 높이 계산을 멈춘다 — 스토어에 두어 각 컴포넌트의 훅도 본다 (2026-10-01)
  const isInteracting = usePdfBuilderStore((s) => s.isInteracting)
  const setIsInteracting = usePdfBuilderStore((s) => s.setInteracting)

  // 키보드 조작은 모아서 한 번에 적용한다 (2026-09-30 보고: 키마다 페이지 전체가 다시 배치되어 버벅임).
  // Shift+방향키는 누르는 동안 목표 크기를 점선 테두리로만 보여 주고 KEY_APPLY_DELAY_MS 뒤에 스토어에 한 번 적용한다.
  // 적용(무거운 재배치)은 "적용 중" 표시가 먼저 그려진 다음 프레임에 시작하고, 그 렌더가 끝나면 표시를 내린다.
  const pendingResizeRef = useRef<{
    itemId: string
    w: number
    h: number
    autoSizeOff: boolean
  } | null>(null)
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [keyGhost, setKeyGhost] = useState<{ x: number; y: number; w: number; h: number } | null>(
    null,
  )
  const [isKeyApplying, setIsKeyApplying] = useState(false)
  // 적응형(2026-09-30 결정): 적용에 걸린 시간(다음 프레임까지)을 재 두고, 직전 것이 SLOW_APPLY_MS 를 넘었을 때만
  // 스피너를 먼저 그린 다음 프레임에 적용한다. 그렇지 않으면 표시 없이 바로 적용한다
  const lastApplyMsRef = useRef(0)
  const runWithBusy = useCallback((apply: () => void) => {
    const applyAndMeasure = () => {
      const started = performance.now()
      apply()
      requestAnimationFrame(() => {
        lastApplyMsRef.current = performance.now() - started
        setIsKeyApplying(false)
      })
    }
    if (lastApplyMsRef.current < SLOW_APPLY_MS) {
      applyAndMeasure()

      return
    }
    setIsKeyApplying(true)
    // 그리기 중 오류 등으로 내리지 못해도 표시가 남지 않게 상한을 둔다
    setTimeout(() => setIsKeyApplying(false), 1500)
    requestAnimationFrame(() => setTimeout(applyAndMeasure, 0))
  }, [])
  useEffect(
    () => () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current)
      }
    },
    [],
  )
  const ghostColWidth = (canvasWidth - 2 * CONTAINER_PADDING[0] - (COLS - 1) * MARGIN[0]) / COLS

  const handleLayoutChange = useCallback(
    (newLayout: Layout) => {
      if (!pageId) return
      // 메아리는 한 번 맞으면 소진한다 — 라이브러리가 충돌로 밀어내린 결과가 예전에 넘겼던 배치와 우연히 같을 때
      // (표 묶음이 장을 늘리는 동안 자주 생긴다) 그것까지 버리면 스토어가 화면과 어긋난다 (2026-09-29)
      const echoIndex = sentLayoutKeysRef.current.indexOf(layoutKeyOf(newLayout))
      if (echoIndex >= 0) {
        sentLayoutKeysRef.current.splice(echoIndex, 1)

        return
      }
      newLayout.forEach((layoutItem: LayoutItem) => {
        const item = items.find((i) => i.id === layoutItem.i)
        if (
          item &&
          (item.x !== layoutItem.x ||
            item.y !== layoutItem.y ||
            item.w !== layoutItem.w ||
            item.h !== layoutItem.h)
        ) {
          updateItemLayout(pageId, layoutItem.i, {
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
          })
        }
      })
    },
    [pageId, items, updateItemLayout],
  )

  const handleItemClick = useCallback(
    (itemId: string, e: React.MouseEvent) => {
      e.stopPropagation()
      selectItem(itemId)
    },
    [selectItem],
  )

  const handleItemDelete = useCallback(
    (itemId: string) => {
      if (!pageId) return
      // 표 묶음(Table·Action Table, 출력 장마다 아이템 하나)은 낱개로 지울 수 없다 — 통째로 지운다
      // (Table: 2026-09-28 요청, Action Table: 2026-09-29 합류)
      const target = items.find((candidate) => candidate.id === itemId)
      const groupId = target?.options.groupId
      const isSplitContainer =
        target?.chartType === "table" ||
        target?.chartType === "map" ||
        (target?.chartType === "sessionDetails" && Array.isArray(target.options.intervalViews))
      if (isSplitContainer && groupId) {
        items
          .filter((candidate) => candidate.options.groupId === groupId)
          .forEach((candidate) => removeItem(pageId, candidate.id))

        return
      }
      removeItem(pageId, itemId)
    },
    [pageId, items, removeItem],
  )

  // 키보드: 방향키 = 위치 이동, Shift+방향키 = 크기 조절, Alt(⌥)를 더하면 5칸씩 (2026-09-29 채택).
  // 한 칸 = 격자 1칸(가로 1/12, 세로 1행). 입력창에 포커스가 있으면 무시한다
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!pageId || !selectedItemId) {
        return
      }
      const target = event.target as HTMLElement | null
      const inEditable =
        !!target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      // Esc = 선택 해제 — 속성 패널 입력창에 포커스가 있어도 듣는다(포커스를 빼고 해제) (2026-09-30 요청)
      if (event.key === "Escape") {
        if (inEditable) {
          target.blur()
        }
        selectItem(null)

        return
      }
      if (inEditable) {
        return
      }
      // Delete/Backspace = 선택한 컴포넌트 삭제 (2026-09-30 요청)
      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault()
        handleItemDelete(selectedItemId)

        return
      }
      const deltas: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      }
      const delta = deltas[event.key]
      if (!delta) {
        return
      }
      const item = items.find((candidate) => candidate.id === selectedItemId)
      if (!item) {
        return
      }
      event.preventDefault()
      const step = event.altKey ? 5 : 1
      const [dx, dy] = delta
      if (event.shiftKey) {
        const pending =
          pendingResizeRef.current?.itemId === item.id ? pendingResizeRef.current : null
        const baseW = pending?.w ?? item.w
        const baseH = pending?.h ?? item.h
        const w = Math.max(item.minW ?? 1, Math.min(COLS - item.x, baseW + dx * step))
        const h = Math.max(item.minH ?? 1, baseH + dy * step)
        if (w === baseW && h === baseH) {
          return
        }
        pendingResizeRef.current = {
          itemId: item.id,
          w,
          h,
          // 높이를 손으로 정한 Action Map 은 자동 높이를 끈다 (마우스 리사이즈와 같은 규칙)
          autoSizeOff:
            (pending?.autoSizeOff ?? false) ||
            (h !== item.h && item.chartType === "map" && item.options.autoSize !== false),
        }
        setKeyGhost({ x: item.x, y: item.y, w, h })
        if (pendingTimerRef.current) {
          clearTimeout(pendingTimerRef.current)
        }
        pendingTimerRef.current = setTimeout(() => {
          pendingTimerRef.current = null
          const next = pendingResizeRef.current
          pendingResizeRef.current = null
          setKeyGhost(null)
          if (!next) {
            return
          }
          runWithBusy(() => {
            updateItemLayout(pageId, next.itemId, { w: next.w, h: next.h })
            if (next.autoSizeOff) {
              const latest = usePdfBuilderStore
                .getState()
                .pages.find((candidate) => candidate.id === pageId)
                ?.items.find((candidate) => candidate.id === next.itemId)
              if (latest) {
                updateItem(pageId, next.itemId, {
                  options: { ...latest.options, autoSize: false },
                })
              }
            }
          })
        }, KEY_APPLY_DELAY_MS)

        return
      }
      // 방향키는 컴포넌트 순서 바꾸기 — 이웃 컴포넌트와 자리를 맞바꾼다 (2026-09-29 요청). 묶음(표 장들)은 한 덩어리로 움직인다
      const blockOf = (target: CanvasItem) =>
        target.options.groupId
          ? items.filter((other) => other.options.groupId === target.options.groupId)
          : [target]
      const seen = new Set<string>()
      const blocks: CanvasItem[][] = []
      items
        .slice()
        .sort((a, b) => a.y - b.y || a.x - b.x)
        .forEach((other) => {
          if (seen.has(other.id)) {
            return
          }
          const block = blockOf(other)
          block.forEach((member) => seen.add(member.id))
          blocks.push(block)
        })
      const top = (block: CanvasItem[]) => Math.min(...block.map((m) => m.y))
      const bottom = (block: CanvasItem[]) => Math.max(...block.map((m) => m.y + m.h))
      const left = (block: CanvasItem[]) => Math.min(...block.map((m) => m.x))
      const right = (block: CanvasItem[]) => Math.max(...block.map((m) => m.x + m.w))
      const mine = blocks.find((block) => block.some((m) => m.id === item.id))
      if (!mine) {
        return
      }
      const others = blocks.filter((block) => block !== mine)
      if (dy !== 0) {
        // 가로로 겹치는 블록 중 바로 위/아래 것과 맞바꾼다
        const overlapping = others.filter(
          (block) => right(block) > left(mine) && left(block) < right(mine),
        )
        const neighbor =
          dy < 0
            ? overlapping
                .filter((block) => bottom(block) <= top(mine))
                .sort((a, b) => bottom(b) - bottom(a))[0]
            : overlapping
                .filter((block) => top(block) >= bottom(mine))
                .sort((a, b) => top(a) - top(b))[0]
        if (!neighbor) {
          return
        }
        const upper = dy < 0 ? neighbor : mine
        const lower = dy < 0 ? mine : neighbor
        const upperTop = top(upper)
        const lowerShift = top(lower) - upperTop
        const upperShift = bottom(lower) - top(lower)
        runWithBusy(() => {
          lower.forEach((m) => updateItemLayout(pageId, m.id, { y: m.y - lowerShift }))
          upper.forEach((m) => updateItemLayout(pageId, m.id, { y: m.y + upperShift }))
        })

        return
      }
      // 세로로 겹치는(같은 줄) 블록 중 바로 왼쪽/오른쪽 것과 맞바꾼다
      const sideBySide = others.filter(
        (block) => bottom(block) > top(mine) && top(block) < bottom(mine),
      )
      const neighbor =
        dx < 0
          ? sideBySide
              .filter((block) => right(block) <= left(mine))
              .sort((a, b) => right(b) - right(a))[0]
          : sideBySide
              .filter((block) => left(block) >= right(mine))
              .sort((a, b) => left(a) - left(b))[0]
      if (!neighbor) {
        return
      }
      const leftBlock = dx < 0 ? neighbor : mine
      const rightBlock = dx < 0 ? mine : neighbor
      const leftEdge = left(leftBlock)
      const rightShift = left(rightBlock) - leftEdge
      const leftShift = right(rightBlock) - left(rightBlock)
      runWithBusy(() => {
        rightBlock.forEach((m) => updateItemLayout(pageId, m.id, { x: m.x - rightShift }))
        leftBlock.forEach((m) => updateItemLayout(pageId, m.id, { x: m.x + leftShift }))
      })
    }
    window.addEventListener("keydown", onKeyDown)

    return () => window.removeEventListener("keydown", onKeyDown)
  }, [
    pageId,
    selectedItemId,
    items,
    updateItemLayout,
    updateItem,
    runWithBusy,
    selectItem,
    handleItemDelete,
  ])

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        selectItem(null)
      }
    },
    [selectItem],
  )

  if (!page) return null

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isPaletteDragEvent(e.dataTransfer)) return
    e.preventDefault()
    setDragDepth((depth) => depth + 1)
  }
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isPaletteDragEvent(e.dataTransfer)) return
    // preventDefault 해야 브라우저가 이 요소를 드롭 대상으로 인정한다
    e.preventDefault()
    e.dataTransfer.dropEffect = "copy"
  }
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isPaletteDragEvent(e.dataTransfer)) return
    setDragDepth((depth) => Math.max(0, depth - 1))
  }
  // 놓은 지점을 격자 칸으로 바꿔 팔레트 클릭(Add)과 같은 규칙으로 아이템을 만든다
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isPaletteDragEvent(e.dataTransfer)) return
    e.preventDefault()
    setDragDepth(0)
    const store = usePdfBuilderStore.getState()
    const paletteItem = store.draggedPaletteItem
    if (!paletteItem || !pageId) return

    const rect = e.currentTarget.getBoundingClientRect()
    const relX = (e.clientX - rect.left) / scale
    const relY = (e.clientY - rect.top) / scale
    const colWidth = (canvasWidth - 2 * CONTAINER_PADDING[0] - (COLS - 1) * MARGIN[0]) / COLS
    const gridX = Math.max(
      0,
      Math.min(
        Math.floor((relX - CONTAINER_PADDING[0]) / (colWidth + MARGIN[0])),
        COLS - paletteItem.defaultWidth,
      ),
    )
    const gridY = Math.max(0, Math.floor((relY - CONTAINER_PADDING[1]) / (ROW_HEIGHT + MARGIN[1])))
    buildItemsFromPalette(paletteItem, gridX, gridY).forEach((newItem) => addItem(pageId, newItem))
    store.setDraggingFromPalette(false)
  }

  return (
    <div
      className={`ReportCanvas ${isLandscape ? "ReportCanvas--landscape" : "ReportCanvas--portrait"}`}
      style={{
        width: canvasWidth,
        minHeight: paperHeight,
      }}
      onClick={handleCanvasClick}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      {isOver && <div className="ReportCanvas__drop-zone ReportCanvas__drop-zone--active" />}

      {items.length === 0 ? (
        <div className="ReportCanvas__empty" style={{ height: canvasHeight }}>
          <RiDragDropLine className="empty-icon" size={48} />
        </div>
      ) : (
        <>
          <GridOverlay
            cols={COLS}
            rowHeight={ROW_HEIGHT}
            margin={MARGIN}
            containerPadding={CONTAINER_PADDING}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            visible={isInteracting}
          />
          {pageBreaks.map(({ top, label }) => (
            <div key={top} className="PageBoundary" style={{ top }}>
              {label && <span className="PageBoundary__label">{label}</span>}
            </div>
          ))}
          {keyGhost && (
            <div
              className="ReportCanvas__key-ghost"
              style={{
                left: CONTAINER_PADDING[0] + keyGhost.x * (ghostColWidth + MARGIN[0]),
                top: CONTAINER_PADDING[1] + keyGhost.y * (ROW_HEIGHT + MARGIN[1]),
                width: keyGhost.w * ghostColWidth + (keyGhost.w - 1) * MARGIN[0],
                height: keyGhost.h * ROW_HEIGHT + (keyGhost.h - 1) * MARGIN[1],
              }}
            />
          )}
          <GridLayout
            className="layout"
            layout={layouts}
            width={canvasWidth}
            gridConfig={{
              cols: COLS,
              rowHeight: ROW_HEIGHT,
              margin: MARGIN,
              containerPadding: CONTAINER_PADDING,
            }}
            dragConfig={{
              enabled: true,
              // 표 머리글을 잡으면 아이템 드래그가 아니라 컬럼 정렬(클릭)이어야 하고, Action Map 의 맵·표 구분선을
              // 잡으면 비율 조절이어야 한다 — 그 외 영역은 그대로 드래그
              cancel: ".MuiDataGrid-columnHeader, .rotation-selector, .BuilderMap__divider",
            }}
            resizeConfig={{
              enabled: true,
              handles: ["n", "e", "s", "w", "ne", "nw", "se", "sw"],
            }}
            // constraints 를 넘기면 라이브러리 기본([gridBounds, minMaxSize])이 통째로 대체된다 — 폭 스냅만 넘겨
            // 아이템의 minH(표의 내용 높이)가 무시되던 문제 (2026-09-28). 기본 둘을 앞에 두고 스냅을 더한다
            constraints={constraints}
            positionStrategy={positionStrategy}
            compactor={undefined}
            onDragStart={() => setIsInteracting(true)}
            onDragStop={(layout) => {
              setIsInteracting(false)
              handleLayoutChange(layout)
            }}
            onResizeStart={(_layout, oldItem) => {
              setIsInteracting(true)
              // 끌기 시작할 때 바로 자동 높이를 끈다 — 끄는 동안 자동 높이가 매 단계 내용 높이로 되돌려 놓으면
              // 손을 따라오지 못하고 느리게 움직인다 (2026-09-29 보고). 놓을 때의 처리(onResizeStop)는 그대로 둔다
              const item = items.find((i) => i.id === oldItem?.i)
              if (pageId && item && item.chartType === "map" && item.options.autoSize !== false) {
                updateItem(pageId, item.id, { options: { ...item.options, autoSize: false } })
              }
            }}
            onResizeStop={(layout, oldItem, newItem) => {
              setIsInteracting(false)
              // 사용자가 표의 높이를 직접 바꾸면 그 아이템의 자동 높이를 끈다 — 내보내기 전후로 자동 높이가
              // 다시 계산되면서 손으로 바꾼 크기가 되돌아가던 문제 (2026-09-28 보고). 스토어의 h 는 이미
              // onLayoutChange 로 바뀌어 있을 수 있어 라이브러리가 주는 이전/이후 배치로 비교한다
              if (pageId && oldItem && newItem && oldItem.h !== newItem.h) {
                const item = items.find((i) => i.id === newItem.i)
                if (item && item.chartType === "map" && item.options.autoSize !== false) {
                  updateItem(pageId, item.id, { options: { ...item.options, autoSize: false } })
                }
              }
              handleLayoutChange(layout)
            }}
            onLayoutChange={handleLayoutChange}>
            {items.map((item) => (
              <div key={item.id} onClick={(e) => handleItemClick(item.id, e)}>
                <CanvasItemComponent
                  item={item}
                  isSelected={selectedItemId === item.id}
                  isRowPeer={rowPeerIds.has(item.id)}
                  isBusy={isKeyApplying && selectedItemId === item.id}
                  onDelete={() => handleItemDelete(item.id)}
                />
              </div>
            ))}
          </GridLayout>
        </>
      )}
    </div>
  )
}
